/* ==========================================================================
   Alwayz Clean Lab - global.js
   Shared utilities + site-wide custom elements (header, drawers, modal,
   carousel, tabs, reveal, tilt, parallax, count-up, accordion, announcement).
   No dependencies. Loaded deferred on every page.
   ========================================================================== */
(() => {
  'use strict';

  const A = (window.Alwayz = window.Alwayz || {});
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const debounce = (fn, wait = 200) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };
  const rafThrottle = (fn) => {
    let queued = false;
    return (...args) => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; fn(...args); });
    };
  };
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

  /* ---------- money ---------- */
  function formatWith(cents, precision, thousands, decimal) {
    if (isNaN(cents) || cents == null) return '0';
    const n = (cents / 100).toFixed(precision);
    const [whole, frac] = n.split('.');
    return whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`) + (frac ? decimal + frac : '');
  }
  function formatMoney(cents, format) {
    format = format || (A.settings && A.settings.moneyFormat) || '₹{{amount}}';
    if (typeof cents === 'string') cents = parseFloat(cents.replace('.', ''));
    const m = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!m) return String(cents / 100);
    let v;
    switch (m[1]) {
      case 'amount_no_decimals': v = formatWith(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': v = formatWith(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': v = formatWith(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': v = formatWith(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': v = formatWith(cents, 0, ' ', '.'); break;
      case 'amount_with_space_separator': v = formatWith(cents, 2, ' ', ','); break;
      default: v = formatWith(cents, 2, ',', '.');
    }
    return format.replace(m[0], v);
  }
  const formatMoneyShort = (cents) => formatMoney(cents).replace(/([.,])00(?!\d)/, '');

  /* ---------- fetch helpers ---------- */
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  async function fetchHTML(url, opts = {}) {
    const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' }, ...opts });
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    return res.text();
  }
  async function fetchJSON(url, opts = {}) {
    const res = await fetch(url, { ...opts, headers: { Accept: 'application/json', ...(opts.headers || {}) } });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { const err = new Error(data.description || data.message || res.statusText); err.status = res.status; err.data = data; throw err; }
    return data;
  }

  /* ---------- scroll lock, focus, toast ---------- */
  let lockCount = 0;
  const lockScroll = () => {
    if (lockCount++ === 0) {
      const sw = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight = sw ? `${sw}px` : '';
      document.body.classList.add('is-locked');
    }
  };
  const unlockScroll = () => {
    if (--lockCount <= 0) {
      lockCount = 0;
      document.body.classList.remove('is-locked');
      document.body.style.paddingRight = '';
    }
  };
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  function trapFocus(container, e) {
    if (e.key !== 'Tab') return;
    const items = $$(FOCUSABLE, container).filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
  let toastTimer;
  function toast(message, ms = 2600) {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      el.classList.remove('is-visible');
      setTimeout(() => { el.hidden = true; }, 400);
    }, ms);
  }

  Object.assign(A, { $, $$, define, debounce, rafThrottle, clamp, formatMoney, formatMoneyShort, parseHTML, fetchHTML, fetchJSON, lockScroll, unlockScroll, trapFocus, toast, reduceMotion, canHover });

  /* ---------- scroll reveal ---------- */
  const reveal = (() => {
    let io;
    const supports = 'IntersectionObserver' in window;
    if (supports) {
      io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
        });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.06 });
    }
    const scan = (root = document) => {
      $$('[data-reveal]:not(.is-visible)', root).forEach((el) => (supports ? io.observe(el) : el.classList.add('is-visible')));
    };
    scan();
    setTimeout(() => $$('[data-reveal]:not(.is-visible)').forEach((el) => { const r = el.getBoundingClientRect(); if (r.top < innerHeight) el.classList.add('is-visible'); }), 2500);
    return { scan };
  })();
  A.reveal = reveal;
  document.addEventListener('shopify:section:load', (e) => reveal.scan(e.target));

  /* ---------- generic drawer + modal ---------- */
  const openStack = [];
  document.addEventListener('keydown', (e) => {
    const top = openStack[openStack.length - 1];
    if (!top) return;
    if (e.key === 'Escape') { e.stopPropagation(); top.close(); }
    else trapFocus(top, e);
  });
  class UIDrawer extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.setAttribute('aria-hidden', 'true');
      this.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) this.close(); });
    }
    get isOpen() { return this.classList.contains('is-open'); }
    open(trigger) {
      if (this.isOpen) return;
      this._trigger = trigger || document.activeElement;
      openStack.push(this);
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll();
      const focusTarget = $('[data-autofocus]', this) || $('[data-drawer-close]', this);
      setTimeout(() => focusTarget && focusTarget.focus({ preventScroll: true }), 50);
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }
    close() {
      if (!this.isOpen) return;
      const at = openStack.indexOf(this);
      if (at > -1) openStack.splice(at, 1);
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      unlockScroll();
      if (this._trigger && this._trigger.focus) this._trigger.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }
  }
  define('ui-drawer', UIDrawer);
  class UIModal extends UIDrawer {}
  define('ui-modal', UIModal);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      const target = document.getElementById(opener.dataset.drawerOpen);
      if (target && target.open) { e.preventDefault(); target.open(opener); }
    }
  });

  /* ---------- header ---------- */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.hideOnScroll = this.dataset.hideOnScroll === 'true';
      this.lastY = window.scrollY;
      this.measure();
      window.addEventListener('resize', debounce(() => this.measure(), 150));
      window.addEventListener('scroll', rafThrottle(() => this.onScroll()), { passive: true });
      this.onScroll();

      this.items = $$('.nav__item.has-mega', this);
      this.items.forEach((item) => {
        const caret = $('.nav__caret', item);
        item.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.openMega(item, true); });
        item.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') this.scheduleClose(); });
        item.addEventListener('focusin', () => clearTimeout(this.closeTimer));
        item.addEventListener('keydown', (e) => { if (e.key === 'Escape') { this.closeMega(); caret && caret.focus(); } });
        if (caret) caret.addEventListener('click', () => (item.classList.contains('is-open') ? this.closeMega() : this.openMega(item, true)));
      });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) { this.closeMega(); this.closeSearch(); } });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') { this.closeMega(); this.closeSearch(); } });

      // search panel
      this.panel = $('[data-search-panel]', this);
      $$('[data-search-open]').forEach((btn) => btn.addEventListener('click', (e) => { e.preventDefault(); this.toggleSearch(btn); }));
      $$('[data-search-close]', this).forEach((btn) => btn.addEventListener('click', () => this.closeSearch()));
      this.overlay = $('[data-search-overlay]', this);
      if (this.overlay) this.overlay.addEventListener('click', () => this.closeSearch());
    }
    measure() {
      const h = this.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--header-h', `${Math.round(this.querySelector('.header__bar').getBoundingClientRect().height)}px`);
      const ann = $('.announce');
      document.documentElement.style.setProperty('--announce-h', ann ? `${ann.offsetHeight}px` : '0px');
      this.height = h;
      this.setSticky();
    }
    setSticky() {
      const top = this.classList.contains('is-hidden') ? 16 : Math.round(this.getBoundingClientRect().height) + 16;
      document.documentElement.style.setProperty('--sticky-top', `${top}px`);
    }
    onScroll() {
      const y = window.scrollY;
      this.classList.toggle('is-scrolled', y > 8);
      if (this.hideOnScroll) {
        const delta = y - this.lastY;
        const busy = this.classList.contains('is-open') || (this.panel && this.panel.classList.contains('is-open')) || $('.nav__item.is-open', this);
        if (!busy && Math.abs(delta) > 6) {
          const hide = delta > 0 && y > this.height * 2;
          if (hide !== this.classList.contains('is-hidden')) { this.classList.toggle('is-hidden', hide); this.setSticky(); }
        }
      }
      this.lastY = y;
    }
    openMega(item, immediate) {
      clearTimeout(this.closeTimer);
      const show = () => {
        this.items.forEach((i) => {
          const on = i === item;
          i.classList.toggle('is-open', on);
          const c = $('.nav__caret', i);
          if (c) c.setAttribute('aria-expanded', String(on));
        });
        this.classList.add('is-open');
      };
      const anyOpen = this.items.some((i) => i.classList.contains('is-open'));
      if (immediate || anyOpen) { clearTimeout(this.openTimer); this.openTimer = setTimeout(show, anyOpen ? 0 : 90); }
    }
    scheduleClose() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.closeMega(), 160);
    }
    closeMega() {
      clearTimeout(this.openTimer);
      (this.items || []).forEach((i) => { i.classList.remove('is-open'); const c = $('.nav__caret', i); if (c) c.setAttribute('aria-expanded', 'false'); });
      if (!this.panel || !this.panel.classList.contains('is-open')) this.classList.remove('is-open');
    }
    toggleSearch(trigger) { (this.panel && this.panel.classList.contains('is-open') ? this.closeSearch() : this.openSearch(trigger)); }
    openSearch(trigger) {
      if (!this.panel) return;
      this.closeMega();
      this.classList.add('is-open');
      this.classList.remove('is-hidden');
      this.panel.classList.add('is-open');
      if (this.overlay) this.overlay.classList.add('is-open');
      $$('[data-search-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
      this._searchTrigger = trigger;
      setTimeout(() => { const input = $('input[type=search]', this.panel); input && input.focus(); }, 120);
    }
    closeSearch() {
      if (!this.panel || !this.panel.classList.contains('is-open')) return;
      this.panel.classList.remove('is-open');
      if (this.overlay) this.overlay.classList.remove('is-open');
      this.classList.remove('is-open');
      $$('[data-search-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    }
  }
  define('site-header', SiteHeader);

  /* ---------- mobile nav accordion ---------- */
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-mnav-toggle]');
    if (!t) return;
    const sub = document.getElementById(t.getAttribute('aria-controls'));
    if (!sub) return;
    const open = t.getAttribute('aria-expanded') !== 'true';
    t.setAttribute('aria-expanded', String(open));
    sub.classList.toggle('is-open', open);
  });

  /* ---------- announcement bar ---------- */
  class AnnounceBar extends HTMLElement {
    connectedCallback() {
      this.slides = $$('.announce__slide', this);
      this.index = 0;
      if (this.slides.length < 2) return;
      const prev = $('[data-announce-prev]', this);
      const next = $('[data-announce-next]', this);
      prev && prev.addEventListener('click', () => this.go(this.index - 1, true));
      next && next.addEventListener('click', () => this.go(this.index + 1, true));
      this.addEventListener('pointerenter', () => this.stop());
      this.addEventListener('pointerleave', () => this.start());
      this.start();
    }
    go(i, manual) {
      this.slides[this.index].classList.remove('is-active');
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides[this.index].classList.add('is-active');
      if (manual) { this.stop(); this.start(); }
    }
    start() { if (reduceMotion) return; this.stop(); this.timer = setInterval(() => this.go(this.index + 1), +(this.dataset.speed || 5) * 1000); }
    stop() { clearInterval(this.timer); }
    disconnectedCallback() { this.stop(); }
  }
  define('announce-bar', AnnounceBar);

  /* ---------- scroll carousel ---------- */
  class ScrollCarousel extends HTMLElement {
    connectedCallback() {
      this.track = $('.carousel__track', this);
      if (!this.track) return;
      this.prev = $('[data-carousel-prev]', this);
      this.next = $('[data-carousel-next]', this);
      this.bar = $('.carousel__progress span', this);
      this.prev && this.prev.addEventListener('click', () => this.page(-1));
      this.next && this.next.addEventListener('click', () => this.page(1));
      this.update = rafThrottle(() => this.sync());
      this.track.addEventListener('scroll', this.update, { passive: true });
      new ResizeObserver(this.update).observe(this.track);
      window.addEventListener('resize', this.update);
      this.enableDrag();
      this.sync();
    }
    page(dir) {
      const first = this.track.children[0];
      if (!first) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = first.getBoundingClientRect().width + gap;
      const visible = Math.max(1, Math.floor((this.track.clientWidth + gap) / step));
      this.track.scrollBy({ left: dir * step * visible, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
    sync() {
      const t = this.track;
      const max = t.scrollWidth - t.clientWidth;
      if (this.prev) this.prev.disabled = t.scrollLeft <= 2;
      if (this.next) this.next.disabled = t.scrollLeft >= max - 2;
      if (this.bar) this.style.setProperty('--progress', String(clamp((t.scrollLeft + t.clientWidth) / Math.max(t.scrollWidth, 1), 0.05, 1)));
      const controls = $('.carousel__controls', this);
      if (controls) controls.hidden = max <= 4;
    }
    enableDrag() {
      let down = false, startX = 0, startLeft = 0, moved = 0;
      const t = this.track;
      t.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = true; moved = 0; startX = e.clientX; startLeft = t.scrollLeft;
      });
      window.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        moved = Math.max(moved, Math.abs(dx));
        if (moved > 5) { t.classList.add('is-dragging'); t.scrollLeft = startLeft - dx; }
      });
      window.addEventListener('pointerup', () => {
        if (!down) return;
        down = false;
        t.classList.remove('is-dragging');
      });
      t.addEventListener('click', (e) => { if (moved > 5) { e.preventDefault(); e.stopPropagation(); moved = 0; } }, true);
      t.addEventListener('dragstart', (e) => e.preventDefault());
    }
  }
  define('scroll-carousel', ScrollCarousel);

  /* ---------- tabs ---------- */
  class TabGroup extends HTMLElement {
    connectedCallback() {
      this.tabs = $$('[role="tab"]', this);
      this.panels = $$('[role="tabpanel"]', this);
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          const k = e.key;
          if (k === 'ArrowRight' || k === 'ArrowLeft' || k === 'Home' || k === 'End') {
            e.preventDefault();
            const n = k === 'Home' ? 0 : k === 'End' ? this.tabs.length - 1 : (i + (k === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
            this.select(n); this.tabs[n].focus();
          }
        });
      });
    }
    select(i) {
      this.tabs.forEach((t, n) => {
        const on = n === i;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        const panel = this.panels[n];
        if (!panel) return;
        panel.hidden = !on;
        panel.classList.toggle('is-entering', on);
      });
      window.dispatchEvent(new Event('resize'));
      reveal.scan(this);
      const link = $('[data-view-all-link]', this);
      const url = this.tabs[i] && this.tabs[i].dataset.viewAll;
      if (link && url) link.href = url;
    }
  }
  define('tab-group', TabGroup);

  /* ---------- accordion (animated <details>) ---------- */
  document.addEventListener('click', (e) => {
    const summary = e.target.closest('summary.acc__summary');
    if (!summary) return;
    const details = summary.parentElement;
    const body = $('.acc__body', details);
    if (!body || reduceMotion) return;
    e.preventDefault();
    const group = details.closest('[data-acc-group]');
    if (details.open) {
      close(details, body);
    } else {
      if (group) $$('details.acc[open]', group).forEach((d) => d !== details && close(d, $('.acc__body', d)));
      details.open = true;
      const h = body.scrollHeight;
      body.animate([{ height: '0px', opacity: 0 }, { height: `${h}px`, opacity: 1 }], { duration: 450, easing: 'cubic-bezier(.22,1,.36,1)' });
    }
    function close(d, b) {
      const h = b.scrollHeight;
      const anim = b.animate([{ height: `${h}px`, opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 320, easing: 'cubic-bezier(.65,0,.35,1)' });
      anim.onfinish = () => { d.open = false; };
    }
  });

  /* ---------- count up ---------- */
  class CountUp extends HTMLElement {
    connectedCallback() {
      const to = parseFloat(this.dataset.to || this.textContent) || 0;
      const suffix = this.dataset.suffix || '';
      const decimals = (String(this.dataset.to).split('.')[1] || '').length;
      const render = (v) => { this.textContent = v.toFixed(decimals) + suffix; };
      if (reduceMotion || !('IntersectionObserver' in window)) { render(to); return; }
      render(0);
      const io = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        const dur = 1600;
        const t0 = performance.now();
        const tick = (now) => {
          const p = clamp((now - t0) / dur, 0, 1);
          render(to * (1 - Math.pow(1 - p, 4)));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }, { threshold: 0.4 });
      io.observe(this);
    }
  }
  define('count-up', CountUp);

  /* ---------- 3D tilt on cards ---------- */
  if (A.settings && A.settings.tilt && canHover && !reduceMotion) {
    document.addEventListener('pointermove', (e) => {
      const el = e.target.closest && e.target.closest('[data-tilt]');
      if (!el || e.pointerType !== 'mouse') return;
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      el.classList.add('is-tilting');
      el.style.setProperty('--ry', `${(x * 9).toFixed(2)}deg`);
      el.style.setProperty('--rx', `${(-y * 9).toFixed(2)}deg`);
    }, { passive: true });
    document.addEventListener('pointerout', (e) => {
      const el = e.target.closest && e.target.closest('[data-tilt]');
      if (el && !el.contains(e.relatedTarget)) {
        el.classList.remove('is-tilting');
        el.style.removeProperty('--rx');
        el.style.removeProperty('--ry');
      }
    });
  }

  /* ---------- magnetic buttons ---------- */
  if (canHover && !reduceMotion) {
    document.addEventListener('pointermove', (e) => {
      const el = e.target.closest && e.target.closest('[data-magnet]');
      if (!el) return;
      const r = el.getBoundingClientRect();
      el.style.transform = `translate(${((e.clientX - r.left) / r.width - 0.5) * 10}px, ${((e.clientY - r.top) / r.height - 0.5) * 8}px)`;
    }, { passive: true });
    document.addEventListener('pointerout', (e) => {
      const el = e.target.closest && e.target.closest('[data-magnet]');
      if (el && !el.contains(e.relatedTarget)) el.style.transform = '';
    });
  }

  /* ---------- parallax stage (hero) ---------- */
  class ParallaxStage extends HTMLElement {
    connectedCallback() {
      if (!(A.settings && A.settings.parallax) || !canHover || reduceMotion) return;
      let tx = 0, ty = 0, x = 0, y = 0, raf = null;
      const loop = () => {
        x += (tx - x) * 0.08; y += (ty - y) * 0.08;
        this.style.setProperty('--mx', x.toFixed(3));
        this.style.setProperty('--my', y.toFixed(3));
        raf = Math.abs(tx - x) + Math.abs(ty - y) > 0.002 ? requestAnimationFrame(loop) : null;
      };
      const host = this.closest('[data-parallax-host]') || this;
      host.addEventListener('pointermove', (e) => {
        const r = host.getBoundingClientRect();
        tx = ((e.clientX - r.left) / r.width - 0.5) * 2;
        ty = ((e.clientY - r.top) / r.height - 0.5) * 2;
        if (!raf) raf = requestAnimationFrame(loop);
      });
      host.addEventListener('pointerleave', () => { tx = 0; ty = 0; if (!raf) raf = requestAnimationFrame(loop); });
    }
  }
  define('parallax-stage', ParallaxStage);

  /* ---------- scroll progress + back to top ---------- */
  const progress = $('.scroll-progress span');
  const toTop = $('[data-back-to-top]');
  if (toTop) toTop.hidden = false;
  const onScroll = rafThrottle(() => {
    const doc = document.documentElement;
    const max = doc.scrollHeight - window.innerHeight;
    if (progress) progress.parentElement.style.setProperty('--scroll', max > 0 ? String(clamp(window.scrollY / max, 0, 1)) : '0');
    if (progress) progress.style.setProperty('--scroll', max > 0 ? String(clamp(window.scrollY / max, 0, 1)) : '0');
    if (toTop) toTop.classList.toggle('is-visible', window.scrollY > 700);
  });
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
  if (toTop) toTop.addEventListener('click', () => window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' }));

  /* ---------- share ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-share]');
    if (!btn) return;
    const data = { title: btn.dataset.shareTitle || document.title, url: btn.dataset.shareUrl || location.href };
    try {
      if (navigator.share) await navigator.share(data);
      else { await navigator.clipboard.writeText(data.url); toast(A.strings.copied); }
    } catch (err) { /* user cancelled */ }
  });

  /* ---------- copy code ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-copy]');
    if (!btn) return;
    try { await navigator.clipboard.writeText(btn.dataset.copy); toast(A.strings.copied); } catch (err) { /* noop */ }
  });
})();
